/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Download, ExternalLink, AlertCircle } from "lucide-react";


/**
 * DocumentViewer — preview in-app de documentos GridFS.
 *
 * Mobile-safe: fetch via axios (carries auth cookie + bearer) → blob URL.
 * Iframes/imgs loading authenticated endpoints directly fail on iOS Safari
 * due to ITP / SameSite cookie restrictions inside iframes.
 *
 * Props:
 *   doc:         { id, filename, content_type, ... }
 *   onClose:     () => void
 *   downloadUrl: string — used for "Abrir em nova janela" + "Descarregar"
 */
export default function DocumentViewer({ doc, onClose, downloadUrl }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ct = (doc?.content_type || "").toLowerCase();
  const isPdf = ct === "application/pdf" || (doc?.filename || "").toLowerCase().endsWith(".pdf");
  const isImg = ct.startsWith("image/") || /\.(jpe?g|png|webp)$/i.test(doc?.filename || "");

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    (async () => {
      try {
        const res = await axios.get(downloadUrl, {
          responseType: "blob",
          withCredentials: true,
        });
        if (cancelled) return;
        createdUrl = window.URL.createObjectURL(res.data);
        setBlobUrl(createdUrl);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(
          status === 401 || status === 403
            ? "Sessão expirada. Atualiza a página e volta a entrar."
            : (e?.response?.data?.detail || "Falha a carregar o documento")
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [doc, downloadUrl]);

  if (!doc) return null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-slate-900 border-slate-700 w-[95vw] max-w-5xl h-[90vh] p-0 flex flex-col overflow-hidden"
        data-testid="di-doc-viewer"
      >
        {/* pr-12: o X de fechar do DialogContent fica no canto superior direito; o diálogo já o tem, não se põe outro */}
        <DialogHeader className="pl-4 pr-12 py-3 border-b border-slate-700 flex flex-row items-center justify-between gap-2 space-y-0">
          <DialogTitle className="text-white text-sm font-semibold truncate flex-1 min-w-0">
            {doc.filename || "Documento"}
          </DialogTitle>
          <div className="flex items-center gap-1 shrink-0">
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-700 hover:border-blue-500/40 hover:bg-slate-800 text-slate-300 hover:text-blue-300"
                data-testid="di-doc-viewer-open-new"
              >
                <ExternalLink className="w-3 h-3" /> Abrir
              </a>
            )}
            {blobUrl && (
              <a
                href={blobUrl}
                download={doc.filename}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-700 hover:border-blue-500/40 hover:bg-slate-800 text-slate-300 hover:text-blue-300"
                data-testid="di-doc-viewer-download"
              >
                <Download className="w-3 h-3" /> Descarregar
              </a>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-950">
          {loading && (
            <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm" data-testid="di-doc-viewer-loading">
              A carregar documento…
            </div>
          )}

          {error && !loading && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center text-slate-300 p-6" data-testid="di-doc-viewer-error">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm">{error}</p>
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 underline"
              >
                Tentar abrir diretamente
              </a>
            </div>
          )}

          {!loading && !error && blobUrl && isPdf && (
            <iframe
              src={blobUrl}
              title={doc.filename}
              className="w-full h-full border-0"
              data-testid="di-doc-viewer-pdf"
            />
          )}

          {!loading && !error && blobUrl && isImg && (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img
                src={blobUrl}
                alt={doc.filename}
                className="max-w-full max-h-full object-contain"
                data-testid="di-doc-viewer-image"
              />
            </div>
          )}

          {!loading && !error && blobUrl && !isPdf && !isImg && (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-400 p-6">
              <p className="text-sm text-center">
                Pré-visualização não suportada para este tipo de ficheiro
                {doc.content_type && <span className="text-slate-500"> ({doc.content_type})</span>}.
              </p>
              <a
                href={blobUrl}
                download={doc.filename}
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs"
              >
                <Download className="w-4 h-4" /> Descarregar
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
